/**
 * Compact ID renderer with a one-click copy. Use in tables where the
 * full UUID is too long for the column (so we show a truncated prefix)
 * but the analyst still wants to copy the real value into a query or
 * Slack message.
 *
 * Falls back to a hidden textarea + document.execCommand when the page
 * isn't served over HTTPS (clipboard API requires secure context).
 */
import React, { useState } from 'react';
import { Button, Tooltip, message } from 'antd';
import { CheckOutlined, CopyOutlined } from '@ant-design/icons';

interface Props {
  /** Full ID to copy. Render-side truncation is controlled separately. */
  value: string | null | undefined;
  /** How many leading characters to show. Defaults to 8. */
  previewLength?: number;
  /** Render a trailing ellipsis after the preview. Defaults to true. */
  showEllipsis?: boolean;
}

async function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fall through to legacy path
    }
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export const CopyableId: React.FC<Props> = ({ value, previewLength = 8, showEllipsis = true }) => {
  const [copied, setCopied] = useState(false);

  if (!value) {
    return <span style={{ color: '#bfbfbf' }}>-</span>;
  }

  const preview = value.length > previewLength ? value.slice(0, previewLength) : value;
  const needsEllipsis = showEllipsis && value.length > previewLength;

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const ok = await copyToClipboard(value);
    if (ok) {
      setCopied(true);
      message.success({ content: 'Copied', duration: 1.2 });
      setTimeout(() => setCopied(false), 1500);
    } else {
      message.error('Copy failed — your browser blocked clipboard access');
    }
  };

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      <Tooltip title={value}>
        <code style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
          {preview}
          {needsEllipsis ? '…' : ''}
        </code>
      </Tooltip>
      <Tooltip title={copied ? 'Copied' : 'Copy full ID'}>
        <Button
          type="text"
          size="small"
          icon={copied ? <CheckOutlined style={{ color: '#52c41a' }} /> : <CopyOutlined />}
          onClick={handleCopy}
          style={{ padding: '0 4px', height: 20, lineHeight: 1 }}
        />
      </Tooltip>
    </span>
  );
};
